import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Badge, Spinner, Table } from 'reactstrap';

import AppSettingsService from 'services/appSettings';
import { ROLE_LABELS } from 'constants/roles';
import { formatUkDateTime } from 'helpers/ukDate';
import { timeAgo } from 'helpers/timeAgo';

/** How often the list is asked for again while the page is open. */
export const REFRESH_INTERVAL_MS = 30 * 1000;

/** The answer, or null when it is not one this page can show (read as a failed refresh). */
function readAnswer(response) {
    const data = response && response.data;

    if (!data || !Array.isArray(data.staff) || !data.serverTime || isNaN(new Date(data.serverTime).getTime())) {
        return null;
    }

    return data;
}

const roleLabel = role => ROLE_LABELS[role] || `Role ${role}`;

/**
 * "Online now" (green) when the server says they were active in the last 5 minutes, otherwise
 * "Last active 2 hours ago", or "Never". The exact UK date and time is on hover. How long ago is
 * worked out from the server's time, never this computer's clock.
 */
function Activity({ member, serverTime }) {
    const testId = `staff-activity-status-${member.id}`;

    if (!member.lastSeenAt) {
        return <span className="text-muted" data-testid={testId}>Never</span>;
    }

    const exact = formatUkDateTime(member.lastSeenAt);

    if (member.online) {
        return <Badge color="success" pill title={exact} data-testid={testId}>Online now</Badge>;
    }

    return <span title={exact} data-testid={testId}>{`Last active ${timeAgo(member.lastSeenAt, serverTime)}`}</span>;
}

/**
 * Staff activity: every Doctor, Admin and Super admin, and when they last used the admin panel.
 * Super admins only - the menu and the route keep everyone else out, and the API refuses them (403).
 *
 * Asks again every 30 seconds while open, never with a request still in flight, and stops when the
 * page is left. A failed refresh keeps the last list and says so on the page (no toast every 30 s).
 */
export default function StaffActivity() {
    // { serverTime, staff }: the last answer that could be shown.
    const [answer, setAnswer] = useState(null);
    // The latest request failed (or its answer could not be read).
    const [failed, setFailed] = useState(false);
    const inFlight = useRef(false);
    const mounted = useRef(false);

    const refresh = useCallback(async () => {
        if (inFlight.current) {
            return;
        }
        inFlight.current = true;

        let next = null;
        try {
            next = readAnswer(await AppSettingsService.getStaffActivity());
        } catch (e) {
            next = null;
        } finally {
            inFlight.current = false;
        }

        if (!mounted.current) {
            return;
        }
        if (next) {
            setAnswer(next);
            setFailed(false);
        } else {
            setFailed(true);
        }
    }, []);

    useEffect(() => {
        mounted.current = true;
        refresh();

        // While the tab is hidden nobody is looking, so don't poll; catch up the moment it is shown again.
        // (The server never counts these polls as the viewer being active either.)
        const tick = () => {
            if (!document.hidden) {
                refresh();
            }
        };
        const onVisibilityChange = () => {
            if (!document.hidden) {
                refresh();
            }
        };
        const timer = setInterval(tick, REFRESH_INTERVAL_MS);
        document.addEventListener('visibilitychange', onVisibilityChange);

        return () => {
            mounted.current = false;
            clearInterval(timer);
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, [refresh]);

    return (
        <div className="page-content mx-2 pb-2">
            <div className="card pr-20px pl-20px pt-20px pb-20px" data-testid="staff-activity-page">
                <label className="details-caption">Staff activity</label>
                <p className="text-muted font-size-12 mb-3">
                    When each Doctor, Admin and Super admin last used the admin panel. &quot;Online now&quot; means
                    active in the last 5 minutes. Hover over a time for the exact date and time (UK time).
                    Updates every 30 seconds.
                </p>

                {failed ? (
                    <Alert color="warning" data-testid="staff-activity-notice">
                        {answer
                            ? `Could not refresh staff activity. Showing the list as it was at ${formatUkDateTime(answer.serverTime)}. `
                            : 'Could not load staff activity. '}
                        Trying again every 30 seconds.
                    </Alert>
                ) : null}

                {!answer && !failed ? (
                    <div className="d-flex justify-content-center py-4" data-testid="staff-activity-loading">
                        <Spinner color="primary" />
                    </div>
                ) : null}

                {answer && answer.staff.length === 0 ? (
                    <p className="text-muted">No staff accounts.</p>
                ) : null}

                {answer && answer.staff.length > 0 ? (
                    <div className="table-responsive">
                        <Table className="table mb-0" data-testid="staff-activity-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Role</th>
                                    <th>Activity</th>
                                </tr>
                            </thead>
                            <tbody>
                                {answer.staff.map(member => (
                                    <tr key={member.id} data-testid={`staff-activity-row-${member.id}`}>
                                        <td>{member.name || '-'}</td>
                                        <td>{roleLabel(member.role)}</td>
                                        <td><Activity member={member} serverTime={answer.serverTime} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                ) : null}
            </div>
        </div>
    );
}
